import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { BootScreen } from './components/BootScreen';
import AppLayout from './pages/AppLayout';
import { CheckinsPage } from './pages/CheckinsPage';
import { HabitsPage } from './pages/HabitsPage';
import { KnowledgePage } from './pages/KnowledgePage';
import LoginPage from './pages/LoginPage';
import PublicPage from './pages/PublicPage';
import { TaskListPage } from './pages/TaskListPage';

/**
 * Drops every cached query when the signed-in account changes. Without this a
 * second account in the same tab is served the previous user's cached data —
 * which both leaks it and pins a workspace id they have no access to, so every
 * workspace-scoped request 403s until a hard reload.
 */
function ResetCacheOnUserChange() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const previousUid = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const uid = user?.uid ?? null;
    if (previousUid.current !== undefined && previousUid.current !== uid) {
      queryClient.clear();
    }
    previousUid.current = uid;
  }, [user?.uid, queryClient]);

  return null;
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, initializing } = useAuth();
  if (initializing) return <BootScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export default function App() {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { retry: 1, refetchOnWindowFocus: true }
        }
      })
  );

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ResetCacheOnUserChange />
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/p/:pageId" element={<PublicPage />} />
            <Route path="/" element={<Navigate to="/app/today" replace />} />
            <Route
              path="/app"
              element={
                <RequireAuth>
                  <AppLayout />
                </RequireAuth>
              }
            >
              <Route index element={<Navigate to="today" replace />} />
              <Route path="today" element={<TaskListPage kind="today" />} />
              <Route path="incoming" element={<TaskListPage kind="incoming" />} />
              <Route path="inbox" element={<TaskListPage kind="inbox" />} />
              <Route path="completed" element={<TaskListPage kind="completed" />} />
              <Route path="project/:id" element={<TaskListPage kind="project" />} />
              <Route path="tag/:id" element={<TaskListPage kind="tag" />} />
              <Route path="checkins" element={<CheckinsPage />} />
              <Route path="habits" element={<HabitsPage />} />
              <Route path="knowledge" element={<KnowledgePage />} />
              <Route path="knowledge/:pageId" element={<KnowledgePage />} />
            </Route>
            <Route path="*" element={<Navigate to="/app/today" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
